package org.example.project.screens_shared

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.engineslot.SlotSnapCompanyApi
import org.example.project.service.YardFlowTimelineApi
import org.example.project.service.YardFlowTimelinePoint
import org.example.project.service.YardFlowTimelineRow
import org.example.project.service.YardLiveSync
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

@Composable
fun SHAREDEndToEndFlow(@Suppress("UNUSED_PARAMETER") art: String = org.example.project.service.currentSaveArt()) {
    Style(SharedWorkspaceStyle)

    var rows by remember { mutableStateOf<List<YardFlowTimelineRow>>(emptyList()) }
    var loading by remember { mutableStateOf(false) }
    var info by remember { mutableStateOf("") }
    val yardLiveVersion = YardLiveSync.version(includeSnap = true)

    fun sortedFlowRows(values: List<YardFlowTimelineRow>): List<YardFlowTimelineRow> =
        values
            .filter { it.flowTyp == "wa_to_we" || it.flowId.isNotBlank() }
            .distinctBy { it.flowId.ifBlank { it.id } }
            .sortedWith(
                compareByDescending<YardFlowTimelineRow> { it.punkte.any(YardFlowTimelinePoint::aktiv) }
                    .thenBy { it.lieferscheinnummer }
            )

    LaunchedEffect(art, yardLiveVersion) {
        loading = true
        info = ""
        val loaded = runCatching {
            val firmenId = SlotSnapCompanyApi.currentFirmenId()
            sortedFlowRows(YardFlowTimelineApi.loadFlowRows(firmenId))
        }
        rows = loaded.getOrElse {
            info = "End-to-End Daten konnten nicht geladen werden."
            emptyList()
        }
        loading = false
    }

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("overflow-y", "auto")
                    property("box-sizing", "border-box")
                }
            }) {
                EndToEndHeader(rows, loading, info)
                if (!loading && rows.isEmpty()) {
                    EmptyEndToEndState()
                } else {
                    rows.forEach { row -> EndToEndFlowCard(row) }
                }
            }
        }
    }
}

@Composable
private fun EndToEndHeader(rows: List<YardFlowTimelineRow>, loading: Boolean, info: String) {
    Div({
        style {
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "center")
            property("gap", "16px")
            property("margin-bottom", "18px")
        }
    }) {
        Div {
            Div({ style { property("font-size", "24px"); property("font-weight", "700"); property("color", "#f8fafc") } }) {
                Text("End-to-End")
            }
            Div({ style { property("font-size", "13px"); property("color", "#94a3b8"); property("margin-top", "4px") } }) {
                Text(if (loading) "Lade WA/WE Ketten ..." else "${rows.size} Flow-Auftraege")
            }
        }
        if (info.isNotBlank()) {
            Div({ style { property("font-size", "13px"); property("color", "#fbbf24"); property("white-space", "nowrap") } }) {
                Text(info)
            }
        }
    }
}

@Composable
private fun EmptyEndToEndState() {
    Div({
        style {
            property("border", "1px solid rgba(148, 163, 184, 0.28)")
            property("background", "rgba(15, 23, 42, 0.62)")
            property("border-radius", "8px")
            property("padding", "22px")
            property("color", "#cbd5e1")
        }
    }) {
        Text("Keine End-to-End WE Flow Auftraege gefunden.")
    }
}

@Composable
private fun EndToEndFlowCard(row: YardFlowTimelineRow) {
    Div({
        style {
            property("border", "1px solid rgba(148, 163, 184, 0.24)")
            property("background", "rgba(15, 23, 42, 0.72)")
            property("border-radius", "8px")
            property("padding", "18px")
            property("margin-bottom", "14px")
            property("box-shadow", "0 14px 30px rgba(15, 23, 42, 0.24)")
        }
    }) {
        EndToEndCardTop(row)
        EndToEndTimeline(row.punkte)
    }
}

@Composable
private fun EndToEndCardTop(row: YardFlowTimelineRow) {
    Div({
        style {
            property("display", "grid")
            property("grid-template-columns", "minmax(220px, 1.4fr) repeat(4, minmax(120px, 0.7fr))")
            property("gap", "12px")
            property("align-items", "start")
            property("margin-bottom", "18px")
        }
    }) {
        HeaderField("LS", row.lieferscheinnummer.ifBlank { row.sourceWaTour })
        HeaderField("WA", "${row.sourceFirmenId}/${row.sourceWaTour}")
        HeaderField("WE", "${row.targetFirmenId}/${row.targetWeId}")
        HeaderField("Paletten", row.palettenanzahl.takeIf { it > 0 }?.toString() ?: "Avis")
        HeaderField("LKW", row.lkwkennzeichen.ifBlank { row.fahrername }.ifBlank { "-" })
    }
}

@Composable
private fun HeaderField(label: String, value: String) {
    Div {
        Div({ style { property("font-size", "11px"); property("color", "#94a3b8"); property("text-transform", "uppercase") } }) {
            Text(label)
        }
        Div({ style { property("font-size", "14px"); property("color", "#f8fafc"); property("font-weight", "650"); property("margin-top", "3px") } }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun EndToEndTimeline(points: List<YardFlowTimelinePoint>) {
    val visiblePoints = points.ifEmpty {
        listOf(YardFlowTimelinePoint(id = "avis", label = "Avis", aktiv = true))
    }
    val activeIndex = activePointIndex(visiblePoints)

    Div({
        style {
            property("position", "relative")
            property("display", "grid")
            property("grid-template-columns", "repeat(${visiblePoints.size}, minmax(94px, 1fr))")
            property("gap", "8px")
            property("padding", "26px 0 16px")
            property("margin", "8px 0 16px")
        }
    }) {
        Div({
            style {
                property("position", "absolute")
                property("left", "3%")
                property("right", "3%")
                property("top", "42px")
                property("height", "2px")
                property("background", "rgba(148, 163, 184, 0.30)")
            }
        })
        visiblePoints.forEachIndexed { index, point ->
            TimelinePoint(point, index == activeIndex)
        }
    }
}

@Composable
private fun TimelinePoint(point: YardFlowTimelinePoint, active: Boolean) {
    Div({
        style {
            property("position", "relative")
            property("min-width", "0")
            property("text-align", "center")
            property("z-index", "1")
        }
    }) {
        if (active) {
            Img("images/lkw.svg") {
                style {
                    property("width", "34px")
                    property("height", "22px")
                    property("object-fit", "contain")
                    property("filter", "brightness(0) invert(1)")
                    property("position", "absolute")
                    property("left", "50%")
                    property("top", "-23px")
                    property("transform", "translateX(-50%)")
                }
            }
        }
        Div({
            style {
                property("width", "18px")
                property("height", "18px")
                property("border-radius", "50%")
                property("margin", "7px auto 9px")
                property("border", "2px solid ${if (active) "#38bdf8" else if (point.erledigt) "#22c55e" else "#64748b"}")
                property("background", if (active) "#0ea5e9" else if (point.erledigt) "#16a34a" else "#0f172a")
                property("box-shadow", if (active) "0 0 0 6px rgba(14, 165, 233, 0.18)" else "none")
            }
        })
        Div({
            style {
                property("font-size", "12px")
                property("line-height", "1.25")
                property("color", if (active) "#f8fafc" else "#cbd5e1")
                property("font-weight", if (active) "700" else "500")
                property("word-break", "break-word")
            }
        }) { Text(point.label.ifBlank { point.key.ifBlank { point.id } }) }
    }
}

private fun activePointIndex(points: List<YardFlowTimelinePoint>): Int {
    val explicit = points.indexOfFirst { it.aktiv }
    if (explicit >= 0) return explicit
    val lastDone = points.indexOfLast { it.erledigt }
    return when {
        lastDone >= 0 && lastDone + 1 < points.size -> lastDone + 1
        lastDone >= 0 -> lastDone
        else -> 0
    }
}
